import React from "react";
import { motion } from "framer-motion";
import { useOverlayScrollLock } from "../../hooks/overlayScrollLock";

type ProfileModalShellProps = {
  children: React.ReactNode;
  onClose: () => void;
  /** Override stacking when opened above another profile-layer modal (e.g. XP hub). */
  zIndexClass?: string;
  overlayAriaLabel?: string;
};

/** Exit-only fade — entering at opacity 0 reads as a blink on desktop. */
const fadeEase = [0.25, 0.1, 0.25, 1] as const;

/** Above react-modal (10010/10011), header overlays (10020), and league sheets. */
const PROFILE_MODAL_Z_CLASS = "z-[10050]";

/**
 * Full-viewport overlay + scrollable panel. Used for every `/profile` view so users stay on context.
 * Wrap with Framer Motion `AnimatePresence` where the modal should animate out on close (e.g. global profile).
 */
export function ProfileModalShell({
  children,
  onClose,
  zIndexClass = PROFILE_MODAL_Z_CLASS,
  overlayAriaLabel = "Close profile",
}: ProfileModalShellProps) {
  useOverlayScrollLock(true);

  return (
    <motion.div
      className={`fixed inset-0 ${zIndexClass} flex items-start justify-center px-3 pb-3 pt-[max(0.5rem,env(safe-area-inset-top,0px))] sm:px-4 sm:pb-4 sm:pt-3`}
      role="dialog"
      aria-modal="true"
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: fadeEase }}
    >
      <button
        type="button"
        data-c8-overlay-dismiss="true"
        className="absolute inset-0 cursor-default border-0 bg-black/75 p-0"
        aria-label={overlayAriaLabel}
        onClick={onClose}
      />
      <div
        data-c8-overlay-scroll
        className="relative z-[1] flex h-[min(92dvh,820px,calc(100dvh-2rem))] w-full max-w-[440px] shrink-0 flex-col overflow-hidden rounded-none border border-white/10 bg-[#461D79] text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </motion.div>
  );
}
